import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  AddResolveDTO,
  AlertResolutionReportDTO,
  DetailedActiveAlertDTO,
  DetailedAlertDTO,
  DetailedPlayerAlertParamsDTO,
  PageDTO,
  PageReq,
  PlayerCommentaryDTO,
  SortBy,
  SortOrder,
  TabName,
} from './models/alerts.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AlertsCollectionAction } from '../../../../components/views/main-table-v2/dto/tickers-dtos';
import { PlayerActivityWsService } from '../../../../common/services/ws/player-activity-service/player-activity-ws.service';
import {
  PlayerActivityServiceCollectionAction,
  PlayerActivityServiceWsDTO,
} from '../../../../common/services/ws/player-activity-service/player-activity-ws.model';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  public selectedTab: string = TabName.ARCHIVE;
  private allElementSubject: Map<string, Subject<any>> = new Map<string, Subject<any[]>>();
  private singleElementSubject: Map<string, Subject<any>> = new Map<string, Subject<any>>();
  private cacheMap: Map<string, Map<number, any>> = new Map<string, Map<number, any>>();
  private alertOutdated = new Subject<boolean>();
  private validationUpdated = new Subject<boolean>();
  private playerActivityWsService = inject(PlayerActivityWsService);

  constructor(private http: HttpClient) {
    this.subscribeToAlertsWs();
  }

  getAlertUpdateListener(): Observable<boolean> {
    return this.alertOutdated.asObservable();
  }

  markAlertAsOutdated(outdated: boolean): void {
    this.alertOutdated.next(outdated);
  }

  getValidationUpdateListener(): Observable<boolean> {
    return this.validationUpdated.asObservable();
  }

  updateValidation(isValid: boolean): void {
    this.validationUpdated.next(isValid);
  }

  private subscribeToAlertsWs() {
    this.playerActivityWsService.getAlertsWsMessageObservable().subscribe((msg) => this.handleCollectionWsMessage(msg));
  }

  private handleCollectionWsMessage(dataFromServer: PlayerActivityServiceWsDTO) {
    switch (dataFromServer.action) {
      case PlayerActivityServiceCollectionAction.UPDATED:
        const messageAddTrigger = dataFromServer.data as DetailedActiveAlertDTO;
        this.addMessageToCollection(dataFromServer.collection, messageAddTrigger, AlertsCollectionAction.UPDATED);
        break;
      case PlayerActivityServiceCollectionAction.CREATED:
        const messageCreateAlert = dataFromServer.data as DetailedActiveAlertDTO;
        this.addMessageToCollection(dataFromServer.collection, messageCreateAlert, AlertsCollectionAction.CREATED);
        break;
      case PlayerActivityServiceCollectionAction.REMOVED:
        const messageRemoveAlert = dataFromServer.data as DetailedActiveAlertDTO;
        this.addMessageToCollection(dataFromServer.collection, messageRemoveAlert, AlertsCollectionAction.REMOVED);
        break;
    }
  }

  addMessageToCollection(collection: string, message: any, actionType: string) {
    this.getOrCreateCollectionMap(collection).set(message.alertId, { type: actionType, data: message });
    this.getOrCreateSingleElementSubject(collection).next(message);
    this.updateAllElementSubject(collection);
  }

  private updateAllElementSubject(collection: string) {
    const values = this.getCollection(collection);
    this.getOrCreateAllElementSubject(collection).next(values);
  }

  private getOrCreateSingleElementSubject(collection: string): Subject<any> {
    const subject = this.singleElementSubject.get(collection);
    if (!subject) {
      this.singleElementSubject.set(collection, new BehaviorSubject(null));
    }
    return this.singleElementSubject.get(collection);
  }

  private getOrCreateCollectionMap(collection: string): Map<number, any> {
    const map = this.cacheMap.get(collection);
    if (!map) {
      this.cacheMap.set(collection, new Map<number, any>());
    }
    return this.cacheMap.get(collection);
  }

  public getCollection(collection: string) {
    const values = this.cacheMap.get(collection)?.values();
    return values ? Array.from(values) : null;
  }

  public getCollectionObservable(collection: string): Observable<any[]> {
    return this.getOrCreateAllElementSubject(collection).asObservable();
  }

  private getOrCreateAllElementSubject(collection: string): Subject<any[]> {
    const subject = this.allElementSubject.get(collection);
    if (!subject) {
      this.allElementSubject.set(collection, new BehaviorSubject(null));
    }
    return this.allElementSubject.get(collection);
  }

  public getPagedAlertsWithDefaultRequest(playerId: number): Observable<PageDTO<DetailedAlertDTO>> {
    const pageReq = {
      number: 0,
      size: 200,
      playerId: playerId,
      sortBy: SortBy.WHEN,
      order: SortOrder.DESC,
      tabName: TabName.ARCHIVE,
    };
    return this.getPagedAlerts(pageReq);
  }

  public getPagedAlerts(req: PageReq): Observable<PageDTO<DetailedAlertDTO>> {
    let params: any = {
      'page-size': req.size,
      // Prevent sending 0 to the api, because its not acceptable and it will brake
      'page-number': req.number > 0 ? req.number : 1,
      'sort-by': req.sortBy ? req.sortBy : SortBy.WHEN,
      'sort-order': req.order ? req.order : SortOrder.DESC,
    };

    if (req.playerId) {
      params = {
        ...params,
        'player-id': req.playerId,
      };
    }

    return this.http
      .get<PageDTO<DetailedAlertDTO>>('/player-activity/api/player-alerts/resolved', {
        params,
      })
      .pipe(
        tap((p) =>
          p.content?.forEach((a) => (a.resolution.outcome = AlertService.figureOutOutcome(a.resolution.actionsTaken)))
        )
      );
  }

  public getDetailedAlert(id: number): Observable<DetailedAlertDTO> {
    return this.http
      .get<DetailedAlertDTO>(`/player-activity/api/player-alerts/${id}`)
      .pipe(tap((a) => (a.resolution.outcome = AlertService.figureOutOutcome(a.resolution.actionsTaken))));
  }

  public getTriggerDocumentation(): Observable<DetailedPlayerAlertParamsDTO> {
    return this.http.get<DetailedPlayerAlertParamsDTO>('/player-activity/api/player-alerts/params');
  }

  public getAlertResolutionReport(alertId: number): Observable<AlertResolutionReportDTO> {
    return this.http.get<AlertResolutionReportDTO>(`/player-activity/api/player-alerts/${alertId}/resolution-report`);
  }

  public generateAlertResolutionReport(alertId: number): Observable<void> {
    return this.http.post<void>(`/player-activity/api/player-alerts/${alertId}/resolution-report/generate`, {});
  }

  public addResolve(alertId: number, resolution: AddResolveDTO): Observable<string> {
    return this.http.post<string>(`/player-activity/api/player-alerts/${alertId}/resolve`, resolution);
  }

  private static figureOutOutcome(actionsTaken: string[]): string {
    return actionsTaken ? actionsTaken.map((a) => a.toLowerCase()).join(', ') : 'No action required';
  }

  public getActiveAlerts(): Observable<DetailedActiveAlertDTO[]> {
    return this.http.get<DetailedActiveAlertDTO[]>('/player-activity/api/player-alerts/active');
  }

  public getPlayerActiveAlerts(playerId: number): Observable<DetailedActiveAlertDTO[]> {
    return this.http.get<DetailedActiveAlertDTO[]>(`/player-activity/api/player-alerts/active?player-id=${playerId}`);
  }

  public setAlertLockState(isLocked: boolean, alertId: number): Observable<any> {
    return !isLocked
      ? this.http.post<any>(`/player-activity/api/player-alerts/${alertId}/lock`, {})
      : this.http.post<any>(`/player-activity/api/player-alerts/${alertId}/unlock`, {});
  }

  public alertUnlock(alertId: number): Observable<any> {
    return this.http.post<any>(`/player-activity/api/player-alerts/${alertId}/unlock`, {});
  }

  public getStarredCommentariesForPlayer(playerId: number): Observable<PlayerCommentaryDTO[]> {
    return this.http.get<PlayerCommentaryDTO[]>(`/player-activity/api/player/${playerId}/starred-commentaries`);
  }
}
